/**
 * IXD 값 편집기 — 워크북 값 불러오기 모달.
 *
 * Figma: 7156:484397(연결/별도 선택 전) · 7108:433681(연결만 선택) ·
 * 7156:484788(둘 다 선택) · 7160:515013(단일 선택 전) · 7156:486038(단일 선택 후).
 *
 * 문서가 연결·별도를 모두 담고 있으면 유형마다 워크북을 하나씩 고를 수 있다 —
 * 그래서 선택 상태가 "하나"가 아니라 "유형 → 워크북" 표다. 다만 한쪽만 골라도
 * 그쪽만 불러올 수 있다.
 * 고른 값을 실제로 채우는 일은 호출부(onConfirm)가 한다. 이 모달은 고르기만 한다.
 */

import { useState } from "react";
import {
  CheckCheck,
  CircleX,
  Loader2,
  Search,
  TriangleAlert,
} from "lucide-react";

import {
  getTypeLabel,
  PeriodChip,
  TypeChip,
  YearChip,
  type ReportPeriod,
  type ReportType,
} from "@/components/common/report-chip";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { logIxdEditorAction } from "@/utils/usage-log";
import { useDSDFiles } from "@/hooks/use-dsd-files";
import { cn } from "@/lib/utils";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import type { DsdFile } from "@/types/dsd";
import { filterBySearchText } from "@/utils/search-text";

/** 유형·기간은 사용자가 올린 DSD 에서 읽은 값이라 빈 문자열이나 낯선 값이 올 수 있다. */
const asReportType = (value: string): ReportType | undefined =>
  value === "CONSOLIDATED" || value === "SEPARATE" ? value : undefined;

const asReportPeriod = (value: string | null): ReportPeriod | undefined =>
  value === "Q1" || value === "Q2" || value === "Q3" || value === "Q4"
    ? value
    : undefined;

/** 워크북 한 줄에 붙는 유형·연도·기간 칩. 읽지 못한 값은 칩을 빼서 추측값을 단언하지 않는다. */
const WorkbookChips = ({
  workbook,
  size,
}: {
  workbook: DsdFile;
  size: "small" | "medium";
}) => {
  const type = asReportType(workbook.financialStatementType);
  const period = asReportPeriod(workbook.reportQuarter);

  return (
    <>
      {type && <TypeChip type={type} size={size} className="shrink-0" />}
      {workbook.reportYear != null && (
        <YearChip year={workbook.reportYear} size={size} className="shrink-0" />
      )}
      {period && (
        <PeriodChip period={period} size={size} className="shrink-0" />
      )}
    </>
  );
};

const WorkbookRow = ({
  workbook,
  isSelected,
  onClick,
}: {
  workbook: DsdFile;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={isSelected}
    onClick={onClick}
    className={cn(
      "flex h-[50px] w-full shrink-0 items-center gap-2 rounded-[8px] border px-3 text-left transition-colors",
      isSelected
        ? "border-primary-color bg-blue-50"
        : "border-line-normal bg-static-white hover:bg-background-neutral-normal",
    )}
  >
    <span className="flex shrink-0 items-center gap-1">
      <WorkbookChips workbook={workbook} size="small" />
    </span>
    {/* w-0: 긴 이름의 min-content 폭 대신 버튼의 남은 폭만 쓰게 한다. */}
    <span className="w-0 flex-1 truncate label-2-medium text-label-normal">
      {workbook.label}
    </span>
    {isSelected && (
      <CheckCheck aria-hidden className="size-5 shrink-0 text-primary-color" />
    )}
  </button>
);

/** 하단 선택 슬롯. 유형 하나당 한 칸이고, 비어 있으면 점선으로 "아직 안 골랐다"를 보여준다. */
const SelectedSlot = ({
  type,
  workbook,
  onClear,
}: {
  type: ReportType;
  workbook: DsdFile | undefined;
  onClear: () => void;
}) =>
  workbook ? (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 rounded-[8px] border border-transparent bg-background-gray-alternative p-3">
      <div className="flex items-center justify-between">
        <TypeChip type={type} size="medium" />
        <button
          type="button"
          aria-label={`${getTypeLabel(type)} 워크북 선택 해제`}
          onClick={onClear}
        >
          <CircleX
            aria-hidden
            className="size-5 fill-label-assistive text-static-white"
          />
        </button>
      </div>
      <p className="truncate label-2-regular text-label-normal">
        {workbook.label}
      </p>
    </div>
  ) : (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 rounded-[8px] border border-dashed border-line-neutral bg-background-gray-alternative p-3">
      {/* self-start: flex 세로축 stretch 로 칩이 칸 너비만큼 늘어나던 것을 막는다 */}
      <TypeChip
        type={type}
        size="medium"
        className="self-start bg-label-assistive"
      />
      <p className="text-center caption-1-regular text-label-alternative">
        워크북을 선택해주세요
      </p>
    </div>
  );

interface WorkbookValueLoadModalProps {
  /**
   * 이 IXD 문서가 담고 있는 재무제표 유형. 연결·별도를 모두 담았으면 두 개를 넘기고,
   * 그러면 유형마다 워크북을 하나씩 골라야 "불러오기"가 열린다.
   * 빈 배열이면 고를 수 있는 칸이 없으므로 "불러오기"는 끝까지 잠겨 있다.
   */
  reportTypes: ReportType[];
  /**
   * 고르기 완료. reportTypes 순서대로 워크북이 들어온다.
   * **이 콜백이 불리는 시점에 모달은 이미 닫혀 있다** — 이 모달은 고르기만 하고,
   * 반영·진행 표시·실패 안내는 호출부가 toast 등으로 맡는다.
   */
  onConfirm: (workbooks: DsdFile[]) => void;
}

export const WorkbookValueLoadModal = ({
  reportTypes,
  onConfirm,
}: WorkbookValueLoadModalProps) => {
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<
    Partial<Record<ReportType, DsdFile>>
  >({});

  const { data, isLoading, isError, refetch } = useDSDFiles();

  // 유형을 못 읽은 워크북은 어느 칸도 채울 수 없으므로 목록에서 뺀다.
  const workbooks = (data ?? []).filter((workbook) => {
    const type = asReportType(workbook.financialStatementType);
    return type != null && reportTypes.includes(type);
  });
  const filtered = filterBySearchText(
    workbooks,
    keyword,
    (workbook) => `${workbook.label} ${workbook.name}`,
  );

  const picked = reportTypes
    .map((type) => selected[type])
    .filter((workbook): workbook is DsdFile => workbook != null);
  // 연결·별도를 모두 담은 문서라도 한쪽만 고르면 그쪽만 불러온다 — 나머지는 다시 열어 고르면 된다.
  const canLoad = picked.length > 0;

  const toggle = (workbook: DsdFile) => {
    const type = asReportType(workbook.financialStatementType);
    if (!type) return;
    setSelected((prev) => ({
      ...prev,
      // 같은 줄을 다시 누르면 해제, 같은 유형의 다른 줄을 누르면 교체된다.
      [type]: prev[type]?.id === workbook.id ? undefined : workbook,
    }));
  };

  return (
    <ModalWrapper className="flex h-[550px] w-[600px] max-w-[600px] flex-col gap-0 rounded-lg border border-line-normal bg-white p-0 shadow-xxlarge">
      <div className="flex items-center px-6 pt-5">
        <h2 className="headline-1-semibold text-label-normal">
          워크북 값 불러오기
        </h2>
      </div>
      <div className="px-6 pb-3 pt-2">
        <p className="label-2-regular text-label-alternative">
          워크북을 선택하면 비어 있는 주석에 넣을 수 있는 숫자와 태그를
          불러옵니다.
          <br />
          불러온 값은 확인 후 직접 반영하세요.
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 px-6 pb-4 pt-2">
        {reportTypes.length > 1 && (
          <div className="flex shrink-0 items-start gap-1 rounded-[8px] bg-amber-100 p-2">
            <TriangleAlert
              aria-hidden
              className="size-[18px] shrink-0 text-status-caution"
            />
            <p className="label-2-medium text-label-neutral">
              이 문서는 <span className="font-semibold">연결과 별도</span>를
              모두 포함합니다. 각각 선택할 수 있고, 한쪽만 선택해도 됩니다.
            </p>
          </div>
        )}

        <label className="flex h-[39px] shrink-0 items-center gap-2 rounded-[8px] border border-line-normal bg-white px-3 transition-colors focus-within:border-primary-color">
          <Search aria-hidden className="size-3.5 shrink-0 text-label-normal" />
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="워크북 이름으로 검색"
            className="w-full bg-transparent body-1-normal-regular text-label-normal outline-none placeholder:text-label-assistive"
          />
        </label>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-label-alternative">
            <Loader2 aria-hidden className="size-4 animate-spin" />
            <span className="body-2-normal-regular">
              워크북 목록을 불러오는 중...
            </span>
          </div>
        ) : isError ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <p className="body-2-normal-regular text-label-alternative">
              워크북 목록을 불러오지 못했습니다.
            </p>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => refetch()}
            >
              다시 시도
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="flex flex-1 items-center justify-center body-2-normal-regular text-label-alternative">
            {workbooks.length === 0
              ? "불러올 수 있는 워크북이 없습니다."
              : "검색 결과가 없습니다."}
          </p>
        ) : (
          // 스크롤바 노출은 Radix 기본값(hover) — 저장소의 다른 ScrollbarArea 와 같다.
          <ScrollbarArea
            className="min-h-0 flex-1"
            // [&>div]: Radix 가 끼우는 display:table 래퍼를 block 으로 — 안 그러면 행의 truncate 가 죽는다
            viewportClassName="[&>div]:!block"
          >
            <div className="flex flex-col gap-1">
              {filtered.map((workbook) => (
                <WorkbookRow
                  key={workbook.id}
                  workbook={workbook}
                  isSelected={picked.some((item) => item.id === workbook.id)}
                  onClick={() => toggle(workbook)}
                />
              ))}
            </div>
          </ScrollbarArea>
        )}

        {picked.length > 0 && (
          <div className="flex shrink-0 flex-col gap-2">
            <div className="h-px w-full bg-line-normal" />
            <div className="flex items-stretch gap-2">
              {reportTypes.map((type) => (
                <SelectedSlot
                  key={type}
                  type={type}
                  workbook={selected[type]}
                  onClear={() =>
                    setSelected((prev) => ({ ...prev, [type]: undefined }))
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5">
        <Button variant="outlined" size="base" onClick={popModal}>
          취소
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="base"
          disabled={!canLoad}
          onClick={() => {
            logIxdEditorAction("워크북 선택 완료", {
              selectedWorkbookIds: picked
                .map((workbook) => workbook.id)
                .join(","),
            });
            popModal();
            onConfirm(picked);
          }}
        >
          불러오기
        </Button>
      </div>
    </ModalWrapper>
  );
};
